import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Box, Theme, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import {
	OrderProduct,
	Shopper,
	SetProduct,
	OrderObject,
	MatchedProduct,
	OrderInfo,
	CurrencyObject,
	PartialWithId,
	PartialOrder,
	PurchaseType,
	PurchaseTypes,
} from 'common/types';
import { ReduxState } from 'services/types';
import AddProduct from '../../AddProduct';
import Grid from '@mui/material/Grid';
import {
	updateActiveShopperId,
	updateSelectedNonSetProductIds,
	updateSelectedSetProducts,
	updateActivePartialOrder,
} from 'actions';
import {
	getSelectedNonSetProductIdsForCurrentShopper,
	getSelectedSetProductsForCurrentShopper,
} from './utils';
import ProductActions from './components/ProductActions';
import PriceButton from './components/PriceButton';
import PriceEditActions from './components/PriceEditActions';
import ProductContainer from './ProductContainer';
import { getOrderPricing, getPricingOptions, getTotalProductPricing } from 'common/modules/orders';
import { useTranslation } from 'services/localization';
import { PriceBreakdown } from 'common/components/molecules/PriceBreakdown';
import { orderHasDeliveryOrPickup } from 'common/modules/delivery/utils';
import { groupBy } from 'lodash';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import { usePermissions } from 'hooks/usePermissions';

interface OwnProps {
	activeRental: OrderObject;
	shopperProducts: OrderProduct[];
	shopper: Shopper;
	onProductsDelete: (
		productIdsToDelete: string[],
		setProductsToDelete: SetProductWithParentId[],
	) => void;
	onShopperDelete: () => void;
	returnProducts: (
		productIdsToReturn: string[],
		setProductsToReturn: SetProductWithParentId[],
	) => void;
	productToSetHighlightFor: MatchedProduct | null;
	currency: CurrencyObject;
	editingShopper: boolean;
	editingPrice: boolean;
	onPriceEdit: (editing: boolean) => void;
}

interface StateToProps {
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
	activeShopperId: string | null;
	rentalInfo: OrderInfo;
}

interface SetProductWithParentId extends SetProduct {
	parentId: string;
}

interface DispatchProps {
	updateActiveShopperId: (shopperId: string) => void;
	updateSelectedSetProducts: (selectedSetProducts: SetProductWithParentId[]) => void;
	updateSelectedNonSetProductIds: (selectedNonSetProductIds: string[]) => void;
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
}

type Props = OwnProps & DispatchProps & StateToProps & WithStyles<typeof styles>;

export const ShopperProducts = ({ ...props }: Props) => {
	const {
		classes,
		shopperProducts: originalShopperProducts,
		shopper,
		activeRental,
		selectedSetProducts,
		selectedNonSetProductIds,
		activeShopperId,
		currency,
		editingShopper,
		editingPrice,
		onPriceEdit,
	} = props;
	const { t, lang } = useTranslation();
	const { hasPermission } = usePermissions();
	const [addProductsDialogOpen, setAddProductsDialogOpen] = useState(false);
	const [productsWithPriceEdits, setProductsWithPriceEdits] = useState<
		PartialWithId<OrderProduct>[]
	>([]);

	const editedShopperProductsMap = productsWithPriceEdits.reduce(
		(prev, curr) => ({ ...prev, [curr.id]: curr.data }),
		{} as { [id: string]: Partial<OrderProduct> },
	);

	const shopperProducts: OrderProduct[] = originalShopperProducts.map((p) => {
		const partialEdit = editedShopperProductsMap[p.id];
		return {
			...p,
			...(partialEdit ?? {}),
		};
	});

	const { rental: rentalProducts = [], sales: salesProducts = [] } = groupBy(
		shopperProducts,
		(p) => p.purchaseType,
	);

	const shopperPricing = getTotalProductPricing(shopperProducts);

	const handleAddProducts = () => {
		props.updateActiveShopperId(shopper.id);
		setAddProductsDialogOpen(true);
	};

	const handleDeleteShopper = () => {
		props.onShopperDelete();
		props.updateSelectedSetProducts([]);
		props.updateSelectedNonSetProductIds([]);
	};

	const handleDeleteProducts = () => {
		props.onProductsDelete(
			getSelectedNonSetProductIdsForCurrentShopper(shopperProducts, selectedNonSetProductIds),
			getSelectedSetProductsForCurrentShopper(shopperProducts, selectedSetProducts),
		);
		props.updateSelectedSetProducts([]);
		props.updateSelectedNonSetProductIds([]);
	};

	const handleReturnProducts = () => {
		props.returnProducts(
			getSelectedNonSetProductIdsForCurrentShopper(shopperProducts, selectedNonSetProductIds),
			getSelectedSetProductsForCurrentShopper(shopperProducts, selectedSetProducts),
		);
		props.updateSelectedSetProducts([]);
		props.updateSelectedNonSetProductIds([]);
	};

	const handleCancelPriceChanges = () => {
		setProductsWithPriceEdits([]);
		onPriceEdit(false);
	};

	const handleSavePriceChanges = () => {
		const updatedOrderProducts = activeRental.products.map((p) => {
			return shopperProducts.find((prod) => prod.id === p.id) ?? p;
		});
		const rentalInfo = activeRental.rentalInfo;
		const updatedOrderPricing = getOrderPricing(
			updatedOrderProducts,
			activeRental.orderDelivery,
			getPricingOptions(activeRental.rentalInfo.pricing),
		);
		props.updateActivePartialOrder({
			products: productsWithPriceEdits,
			rentalInfo: {
				id: rentalInfo.id,
				data: {
					pricing: updatedOrderPricing,
				},
			},
		});
		setProductsWithPriceEdits([]);
		onPriceEdit(false);
	};

	const hasNegativePrices = (products: OrderProduct[]) => {
		return products.some((p) => p.pricing.total < 0 || (p.pricing.deposit ?? 0) < 0);
	};

	const handleProductPriceEdit = (partialProduct: PartialWithId<OrderProduct>) => {
		setProductsWithPriceEdits((currEdits) => {
			let itemFound = false;
			const newEdits = currEdits.map((i) => {
				if (i.id === partialProduct.id) {
					itemFound = true;
					return {
						id: i.id,
						data: {
							...i.data,
							...partialProduct.data,
						},
					};
				}
				return i;
			});
			return itemFound ? newEdits : [...newEdits, partialProduct];
		});
	};

	const renderProductContainer = (products: OrderProduct[], purchaseType: PurchaseType) => {
		return (
			!!products.length && (
				<Box
					mt={2}
					pl={2}
					sx={{
						borderLeft: (theme) =>
							purchaseType === PurchaseTypes.sales
								? `4px solid ${theme.palette.primary.light}`
								: 'none',
					}}
				>
					{purchaseType === PurchaseTypes.sales && (
						<Typography variant="subtitle1" color="primary">
							{t('common:states.sold', 'Sold')}
						</Typography>
					)}

					{products.map((product) => (
						<ProductContainer
							key={product.id}
							product={product}
							shopper={shopper}
							selectedNonSetProductIds={selectedNonSetProductIds}
							selectedSetProducts={selectedSetProducts}
							productToSetHighlightFor={props.productToSetHighlightFor}
							editingPrice={editingPrice}
							editingShopper={editingShopper}
							currency={currency}
							setProductPriceEdit={handleProductPriceEdit}
						/>
					))}
				</Box>
			)
		);
	};
	return (
		<div>
			<Grid
				spacing={2}
				container
				justifyContent="flex-end"
				className={classes.productActionContainer}
			>
				<Grid item>
					<PriceButton
						currency={currency}
						totalPrice={shopperPricing.total}
						totalDeposit={shopperPricing.deposit}
						editingPrice={editingPrice}
						setEditingPrice={onPriceEdit}
						disabled={editingShopper || !hasPermission('edit_booking')}
					/>
				</Grid>
				<Grid item>
					{!editingPrice ? (
						<ProductActions
							shopperProducts={shopperProducts}
							selectedNonSetProductIds={selectedNonSetProductIds}
							selectedSetProducts={selectedSetProducts}
							rentalState={activeRental.rentalInfo.rentalState}
							handleDeleteShopper={handleDeleteShopper}
							handleDeleteProducts={handleDeleteProducts}
							handleReturnProducts={handleReturnProducts}
							handleAddProducts={handleAddProducts}
							hasDelivery={orderHasDeliveryOrPickup(activeRental.orderDelivery)}
							shopper={shopper}
							editingShopper={editingShopper}
						/>
					) : (
						<PriceEditActions
							handleCancelPriceChanges={handleCancelPriceChanges}
							handleSavePriceChanges={handleSavePriceChanges}
							disableSave={hasNegativePrices(shopperProducts) || !hasPermission('edit_booking')}
						/>
					)}
				</Grid>
			</Grid>
			{renderProductContainer(rentalProducts, PurchaseTypes.rental)}
			{renderProductContainer(salesProducts, PurchaseTypes.sales)}
			{editingPrice && (
				<Box pt={2}>
					<PriceBreakdown
						pricing={shopperPricing}
						shopCurrency={currency}
						t={t}
						styling={{ totalDivider: true }}
						lang={lang}
					/>
				</Box>
			)}
			{shopperPricing.total < 0 && (
				<Grid item xs={12}>
					<Typography variant="body2" color="error" align="right">
						{t('discountDialog.invalidNegativePrice', 'Price cannot be negative')}
					</Typography>
				</Grid>
			)}
			{addProductsDialogOpen && (
				<AddProduct
					open={addProductsDialogOpen}
					activeRental={activeRental}
					activeShopperId={activeShopperId}
					handleClose={() => setAddProductsDialogOpen(false)}
				/>
			)}
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		productActionContainer: {
			textAlign: 'right',
			marginBottom: theme.spacing(1),
			justifyContent: 'space-between',
		},
	});

const mapStateToProps = (state: ReduxState): StateToProps => {
	const rentalInfo = RentalViewSelectors.orderInfo(state)!;
	return {
		selectedNonSetProductIds: state.rentalView.selectedNonSetProductIds,
		selectedSetProducts: state.rentalView.selectedSetProducts,
		activeShopperId: state.rentalView.activeShopperId,
		rentalInfo,
	};
};

const dispatchProps = {
	updateActiveShopperId,
	updateSelectedNonSetProductIds,
	updateSelectedSetProducts,
	updateActivePartialOrder,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(ShopperProducts));
